import React, { useEffect, useState, useMemo } from 'react'

import { ContainerRoot } from '../ContainerRoot'
import { Tab, Tabs, useTheme } from '@material-ui/core'
import { MoveInspectionContainer } from '../DamageHome/MoveInspectionContainer'
import { useLeaseFilter } from '../../hooks/useLeaseFilter'
import { LeaseFilterDrawer } from './Forms/LeaseFilterDrawer'
import {
    useCompany,
    useLease,
    useRootInfrastructure,
    useSchedule,
    useService,
    useUser,
    useAptConfig,
} from '../../hooks'
import { Lease, LeaseStatusType, Unit } from '../../models'
import {
    Container,
    DEFAULT_DRAWER_GUTTER,
    DEFAULT_DRAWER_WIDTH,
    SideDrawerContainer,
} from '../../components'
import { axiosInstance } from '../../helpers'
import { LeaseHomeLeasesTab } from './Tabs/LeaseHome/LeaseHomeLeasesTab'
import { LeaseHomeIssuesTab } from './Tabs/LeaseHome/LeaseHomeIssuesTab'

const LEASES_TAB = 0
const MOVE_IN_TAB = 1
const ISSUES_TAB = 2

export const LeaseHome = () => {
    const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null)

    const {
        dateFilter,
        setDateFilter,
        locationFinderSelection,
        filterDrawerOpen,
        searchValue,
        setSearchValue,
        setFilterDrawerOpen,
        getFilteredLeases,
        statusFilter,
        setStatusFilter,
        setIssueFilter,
        issueFilter,
        issueWorkorderFilter,
        setIssueWorkorderFilter,
        tenantInspectionStatusFilter,
        setTenantInspectionStatusFilter,
        autoCompleteFilter,
        setAutoCompleteFilter,
        issueHasImageFilter,
        setIssueHasImageFilter,
        petFilter,
        setPetFilter,
    } = useLeaseFilter()

    const {
        leaseList,
        getLeaseList,
        transitionLease,
        isLoading,
        setLeaseList,
        updateLeaseState,
        updateLease,
        createLeaseMoveInInvItems,
    } = useLease()

    const {
        leaseList: childLeaseList,
        getLeaseList: getChildLeaseList,
    } = useLease()

    const { userList, actions } = useUser()
    const { getUserList } = actions

    const { serviceList } = useService({ getServiceList: true, cleanUp: true })
    const { vendorList } = useCompany({ getVendorList: true, cleanUp: true })

    const { scheduleList, getScheduleList } = useSchedule()

    const {
        getUnitConfigMap,
        getAreaConfigMap,
        getInventoryConfigMap,
    } = useAptConfig({
        unitConfigList: true,
        areaConfigList: true,
        inventoryConfigList: true,
        cleanUp: true,
    })

    const getLastSyncDate = () => {
        axiosInstance
            .get(`stats/reports/`, {
                params: { report_types: 'LEASE_SYNC_REPORT' },
            })
            .then((res) => {
                if (res.data.length > 0) {
                    const lastReport = res.data[0]
                    const creationTimeStr = lastReport.creation_time
                    const lastSyncDate = new Date(creationTimeStr)
                    setLastSyncDate(lastSyncDate)
                }
            })
    }

    // Load the leases
    useEffect(() => {
        let statusStr = ''
        Object.keys(statusFilter).forEach((key) => {
            const status: LeaseStatusType = Number(key) as LeaseStatusType
            if (statusFilter[status]) {
                statusStr += `${status},`
            }
        })

        if (statusStr === '') {
            statusStr = '1,2,3,4,5,6'
        }

        const parentPromise = getLeaseList({
            params: {
                statuses: statusStr,
                home: true,
                is_child: false,
            },
        })
        const childPromise = getChildLeaseList({
            params: {
                statuses: statusStr,
                home: true,
                is_child: true,
            },
        })

        Promise.all([childPromise, parentPromise]).then((promiseRes) => {
            const childLeases = promiseRes[0]
            const parentLeases = promiseRes[1]

            // Create a map of parent ID to children for efficient lookup
            const childrenByParent = new Map<number, Lease[]>()
            childLeases.forEach((child) => {
                if (child.parent) {
                    if (!childrenByParent.has(child.parent)) {
                        childrenByParent.set(child.parent, [])
                    }
                    childrenByParent.get(child.parent)!.push(child)
                }
            })

            const newParentLeases = parentLeases.map((lease) => {
                const innerChildren = childrenByParent.get(lease.id) || []
                return { ...lease, children: innerChildren }
            })

            setLeaseList(newParentLeases)
        })
    }, [statusFilter])

    // Load the last sync date
    useEffect(() => {
        getLastSyncDate()
        getUserList({ params: { my_team: true } })
        getScheduleList({})
    }, [])

    const { tree, unitCount } = useRootInfrastructure(true)

    // Build unit map from tree
    const unitMap = useMemo(() => {
        const map: { [key: number]: Unit } = {}
        if (tree.root) {
            const traverse = (folder: any) => {
                folder.units?.forEach((unit: Unit) => {
                    map[unit.id] = unit
                })
                folder.children?.forEach(traverse)
            }
            traverse(tree.root)
        }
        return map
    }, [tree])

    const [tab, setTab] = useState(LEASES_TAB)
    const theme = useTheme()

    return (
        <ContainerRoot
            style={{ overflow: 'hidden', minWidth: 0, width: '100%' }}
        >
            <SideDrawerContainer
                open={filterDrawerOpen}
                width={DEFAULT_DRAWER_WIDTH + DEFAULT_DRAWER_GUTTER}
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <Tabs
                        style={{
                            backgroundColor: theme.palette.grey[200],
                            margin: theme.spacing(1),
                            flexShrink: 0,
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                        value={tab}
                        onChange={(_, v) => {
                            setSearchValue('')
                            setTab(v)
                        }}
                    >
                        <Tab label="Leases" value={LEASES_TAB} />
                        <Tab label="Move In Inspections" value={MOVE_IN_TAB} />
                        <Tab label="Issues" value={ISSUES_TAB} />
                    </Tabs>
                    <Container
                        style={{
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        {tab === LEASES_TAB && (
                            <LeaseHomeLeasesTab
                                getFilteredLeases={getFilteredLeases}
                                statusFilter={statusFilter}
                                filterDrawerOpen={filterDrawerOpen}
                                setFilterDrawerOpen={setFilterDrawerOpen}
                                setSearchValue={setSearchValue}
                                searchValue={searchValue}
                                dateFilter={dateFilter}
                                setDateFilter={setDateFilter}
                                locationFinderSelection={
                                    locationFinderSelection
                                }
                                setStatusFilter={setStatusFilter}
                                tree={tree}
                                unitCount={unitCount}
                                leaseList={leaseList}
                                setLeaseList={setLeaseList}
                                isLoading={isLoading}
                                transitionLease={transitionLease}
                                hasIssueFilter={issueFilter}
                                setHasIssue={setIssueFilter}
                                tenantInspectionStatusFilter={
                                    tenantInspectionStatusFilter
                                }
                                setTenantInspectionStatusFilter={
                                    setTenantInspectionStatusFilter
                                }
                                updateLeaseState={updateLeaseState}
                                autoCompleteFilter={autoCompleteFilter}
                                setAutoCompleteFilter={setAutoCompleteFilter}
                                lastSyncDate={lastSyncDate}
                                updateLease={updateLease}
                                petFilter={petFilter}
                                setPetFilter={setPetFilter}
                            />
                        )}
                        {tab === MOVE_IN_TAB && (
                            <MoveInspectionContainer
                                getFilteredLeases={getFilteredLeases}
                                filterDrawerOpen={filterDrawerOpen}
                                setFilterDrawerOpen={setFilterDrawerOpen}
                                setSearchValue={setSearchValue}
                                tree={tree}
                                leaseList={leaseList}
                                isLoading={isLoading}
                                dateFilter={dateFilter}
                                setDateFilter={setDateFilter}
                                locationFinderSelection={
                                    locationFinderSelection
                                }
                                statusFilter={statusFilter}
                                setStatusFilter={setStatusFilter}
                                hasIssue={issueFilter}
                                setHasIssue={setIssueFilter}
                                tenantInspectionStatusFilter={
                                    tenantInspectionStatusFilter
                                }
                                setTenantInspectionStatusFilter={
                                    setTenantInspectionStatusFilter
                                }
                                searchValue={searchValue}
                                autoCompleteFilter={autoCompleteFilter}
                                setAutoCompleteFilter={setAutoCompleteFilter}
                                createLeaseMoveInInvItems={
                                    createLeaseMoveInInvItems
                                }
                                serviceList={serviceList}
                                vendorList={vendorList}
                                userList={userList}
                                scheduleList={scheduleList}
                                petFilter={petFilter}
                                setPetFilter={setPetFilter}
                            />
                        )}
                        {tab === ISSUES_TAB && (
                            <LeaseHomeIssuesTab
                                getFilteredLeases={getFilteredLeases}
                                leaseList={leaseList}
                                unitMap={unitMap}
                                areaConfigMap={getAreaConfigMap()}
                                invConfigMap={getInventoryConfigMap()}
                                filterDrawerOpen={filterDrawerOpen}
                                setFilterDrawerOpen={setFilterDrawerOpen}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                dateFilter={dateFilter}
                                setDateFilter={setDateFilter}
                                locationFinderSelection={
                                    locationFinderSelection
                                }
                                statusFilter={statusFilter}
                                setStatusFilter={setStatusFilter}
                                hasIssue={issueFilter}
                                setHasIssue={setIssueFilter}
                                issueWorkorderFilter={issueWorkorderFilter}
                                setIssueWorkorderFilter={
                                    setIssueWorkorderFilter
                                }
                                tenantInspectionStatusFilter={
                                    tenantInspectionStatusFilter
                                }
                                setTenantInspectionStatusFilter={
                                    setTenantInspectionStatusFilter
                                }
                                autoCompleteFilter={autoCompleteFilter}
                                setAutoCompleteFilter={setAutoCompleteFilter}
                                isLoading={isLoading}
                                serviceList={serviceList}
                                vendorList={vendorList}
                                userList={userList}
                                scheduleList={scheduleList}
                                onLeaseUpdate={updateLeaseState}
                                issueHasImageFilter={issueHasImageFilter}
                                setIssueHasImageFilter={setIssueHasImageFilter}
                                petFilter={petFilter}
                                setPetFilter={setPetFilter}
                            />
                        )}
                    </Container>
                </Container>
            </SideDrawerContainer>

            <LeaseFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                root={tree.root}
                dateFilter={dateFilter}
                onChangeDateFilter={setDateFilter}
                locationFinderSelection={locationFinderSelection}
                hasIssueFilter={issueFilter}
                setHasIssueFilter={setIssueFilter}
                issueWorkorderFilter={issueWorkorderFilter}
                setIssueWorkorderFilter={setIssueWorkorderFilter}
                tenantInspectionStatusFilter={tenantInspectionStatusFilter}
                setTenantInspectionStatusFilter={
                    setTenantInspectionStatusFilter
                }
                autoCompleteFilter={autoCompleteFilter}
                setAutoCompleteFilter={setAutoCompleteFilter}
                issueHasImageFilter={issueHasImageFilter}
                setIssueHasImageFilter={setIssueHasImageFilter}
                petFilter={petFilter}
                setPetFilter={setPetFilter}
            />
        </ContainerRoot>
    )
}
